import { useRef, useState } from 'react'

import { isAdminRoutePathname } from '@bettermode/domain/routes/web/admin'
import { isModerationRoutePathname } from '@bettermode/domain/routes/web/moderation'
import { BillingPriceInterval } from '@tribeplatform/gql-client/types'
import { useRouterPath } from '@tribeplatform/react-sdk'
import { useBillingProducts } from '@tribeplatform/react-sdk/hooks'
import { Modal } from '@tribeplatform/react-ui-kit/Modal'

import {
  TOGGY_BANNER_HEIGHT_VAR,
  TOP_NAVBAR_HEIGHT,
} from '../../TopNavBar/index.js'
import { PlanCardsSection } from '../PlanCards/PlanCardsSection.js'

import { TrialMessagePanel } from './TrialMessagePanel.js'
import { useEnforceModalPresence } from './useEnforceModalPresence.js'

export const TrialExpiredModal = () => {
  const overlayRef = useRef<HTMLDivElement>(null)
  const [billingInterval, setBillingInterval] = useState(
    BillingPriceInterval.Year,
  )
  const { data: products, isInitialLoading: productsLoading } =
    useBillingProducts()
  // Only public plans, matching the plans page — a locked-out customer must not be shown
  // non-public or legacy plans (which may also lack a default price at an interval).
  const plans = products?.plans?.filter(plan => plan.public) ?? []
  useEnforceModalPresence({ modalRef: overlayRef })

  /**
   * Lower the overlay so the bar above it stays visible and clickable, letting
   * a locked admin navigate to their other communities (or log out). Admin and
   * moderation routes render the fixed-height admin bar; the member site uses a
   * dynamic header tracked by the --c-header-height CSS variable.
   */
  const pathname = useRouterPath()
  const showsAdminBar =
    isAdminRoutePathname(pathname) || isModerationRoutePathname(pathname)
  const topOffset = showsAdminBar
    ? `calc(${TOP_NAVBAR_HEIGHT} + var(${TOGGY_BANNER_HEIGHT_VAR}, 0px))`
    : 'var(--c-header-height)'
  /**
   * Lower the whole overlay (panel container + backdrop) so the bar above it
   * stays visible and clickable.
   */
  const offsetStyle = { top: topOffset }
  /**
   * Pin the panel to the region below the bar (`2rem` = the container's top +
   * bottom `p-4`) so it always fills it — no centered top/bottom gaps on wide
   * screens — and its own `overflow-y-auto` scrolls when content is taller.
   */
  const panelStyle = { height: `calc(100dvh - ${topOffset} - 2rem)` }

  return (
    <Modal
      open
      onClose={() => null}
      size="full"
      showCloseBtn={false}
      closeOnBackdropClick={false}
      scrollable={false}
      overlayStyle={offsetStyle}
      backdropStyle={offsetStyle}
      className="p-4 transition-none"
      panelClassName="min-h-0 transition-none"
      panelStyle={panelStyle}
    >
      <div
        ref={overlayRef}
        className="@container/trial-expired my-auto max-w-full px-6 py-8 sm:px-8 lg:px-12 lg:py-10"
      >
        <div className="grid gap-8 @6xl/trial-expired:grid-cols-[max-content_minmax(0,1fr)] @6xl/trial-expired:items-start @6xl/trial-expired:gap-10">
          <div className="@6xl/trial-expired:pt-4">
            <TrialMessagePanel />
          </div>

          <div className="min-w-0">
            <PlanCardsSection
              isLoading={productsLoading}
              plans={plans}
              selectedInterval={billingInterval}
              onIntervalChange={setBillingInterval}
            />
          </div>
        </div>
      </div>
    </Modal>
  )
}
